// src/context/NotificationContext.jsx
import React, { createContext, useContext } from 'react';
import toast from 'react-hot-toast';

export const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const showSuccess = (message, options = {}) => {
    toast.success(message, {
      duration: 3000,
      position: 'top-right',
      style: {
        background: '#10b981',
        color: '#fff',
        fontWeight: '500',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#10b981',
      },
      ...options
    });
  };

  const showError = (message, options = {}) => {
    toast.error(message, {
      duration: 5000,
      position: 'top-right',
      style: {
        background: '#ef4444',
        color: '#fff',
        fontWeight: '500',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#ef4444',
      },
      ...options
    });
  };

  const showWarning = (message, options = {}) => {
    toast((t) => (
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-gray-700">{message}</p>
        </div>
      </div>
    ), {
      duration: 4000,
      position: 'top-right',
      style: {
        background: '#fff',
        color: '#374151',
        border: '1px solid #fbbf24',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      ...options
    });
  };

  const showInfo = (message, options = {}) => {
    toast((t) => (
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-gray-700">{message}</p>
        </div>
      </div>
    ), {
      duration: 3000,
      position: 'top-right',
      style: {
        background: '#fff',
        color: '#374151',
        border: '1px solid #3b82f6',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      ...options
    });
  };

  // Helper function untuk custom toast
  const showCustom = (content, options = {}) => {
    return toast.custom(content, {
      duration: 4000,
      position: 'top-right',
      ...options
    });
  };

  // Helper function untuk loading toast
  const showLoading = (message = 'Loading...') => {
    return toast.loading(message, {
      position: 'top-right',
      style: {
        background: '#374151',
        color: '#fff',
      }
    });
  };

  // Helper function untuk dismiss toast
  const dismiss = (toastId) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  };

  // BACKWARD COMPATIBILITY: Add addNotification function
  // This maintains compatibility with existing components that use addNotification
  const addNotification = (message, type = 'info', options = {}) => {
    switch (type) {
      case 'success':
        showSuccess(message, options);
        break;
      case 'error':
        showError(message, options);
        break;
      case 'warning':
        showWarning(message, options);
        break;
      case 'info':
      default:
        showInfo(message, options);
        break;
    }
  };

  const value = {
    // New method names (recommended)
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showCustom,
    showLoading,
    dismiss,
    // Backward compatibility
    addNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
